/** @format */

/**
 * 会话分析 — 列表侧栏筛选（布局与 TraceSearch 一致）。
 * 文本条件走 FilterItem + getFieldOptions；数值区间走 FilterRangeItem。
 */

import React from 'react'
import { _$, Button } from '@king-design/react'
import styled from 'styled-components'
import { cloneDeep } from 'lodash'
import { MatchMode } from '../typescript'
import {
  SessionFilterItem,
  isSessionMatchFilter,
  isSessionRangeFilter,
  getSessionMatchFilter,
  getSessionRangeFilter
} from '../typescript'
import { ServiceOverviewSearchProps } from 'src/pages/AIObservability/components/typescript'
import FilterItem from '../../components/FilterItem'
import FilterRangeItem from '../../components/FilterRangeItem'

/** 文本匹配筛选项配置，key 与后端 Filters.Key 一致 */
const SESSION_MATCH_OPTIONS: { label: string; key: string; isInput?: boolean }[] = [
  { key: 'service_name', label: _$('服务名称') },
  { key: 'session_id', label: _$('会话 ID'), isInput: true }
]

/** 数值区间筛选项配置 */
const SESSION_RANGE_OPTIONS: { label: string; key: string; suffix?: string }[] = [
  { key: 'duration_ms', label: _$('耗时'), suffix: 'ms' },
  { key: 'total_tokens', label: _$('Total Tokens') },
  { key: 'input_tokens', label: _$('输入Token') },
  { key: 'output_tokens', label: _$('输出Token') },
  { key: 'trace_count', label: _$('调用链数') }
]

export interface SessionSearchProps {
  className?: string
  /** 路由带入的服务名，会锁定 service_name 筛选 */
  serviceName?: string
  /** 顶栏时间范围，用于拉取字段候选值 */
  headerSearch: ServiceOverviewSearchProps
  /** 当前 Filters 数组 */
  value: SessionFilterItem[]
  onChange: (next: SessionFilterItem[]) => void
}

const SessionSearch: React.FC<SessionSearchProps> = ({
  className,
  serviceName,
  headerSearch,
  value,
  onChange
}) => {
  const startTime = headerSearch?.time?.[0]
  const endTime = headerSearch?.time?.[1]
  const matchFilters = value.filter(isSessionMatchFilter)

  /** 更新单条文本匹配，保留区间项不变 */
  const handleMatchChange = (Key: string, Op: MatchMode, Values: string[]) => {
    const rangeFilters = value.filter(isSessionRangeFilter)
    const nextMatch = cloneDeep(matchFilters)
    const idx = nextMatch.findIndex(item => item.Key === Key)
    if (idx !== -1) {
      nextMatch[idx].Op = Op
      nextMatch[idx].Values = Values
    } else {
      nextMatch.push({ Key, Op, Values })
    }
    onChange([...rangeFilters, ...nextMatch])
  }

  /** 更新单条数值区间，保留文本匹配项不变 */
  const handleRangeChange = (
    key: string,
    patch: Partial<{ Start: number | null; End: number | null }>
  ) => {
    const matchItems = value.filter(isSessionMatchFilter)
    const nextRange = cloneDeep(value.filter(isSessionRangeFilter))
    const idx = nextRange.findIndex(item => item.Key === key)
    if (idx !== -1) {
      nextRange[idx] = { ...nextRange[idx], ...patch }
    } else {
      nextRange.push({
        Key: key,
        Start: patch.Start || null,
        End: patch.End || null
      })
    }
    onChange([...matchItems, ...nextRange])
  }

  return (
    <div className={className}>
      <div className="session-search-title">
        <span>{_$('快捷筛选')}</span>
        <Button
          type="link"
          onClick={() => {
            onChange(
              !!serviceName
                ? [
                    {
                      Key: 'service_name',
                      Op: 'in',
                      Values: [serviceName]
                    }
                  ]
                : []
            )
          }}
        >
          {_$('重置')}
        </Button>
      </div>
      <div className="session-search-form">
        {SESSION_MATCH_OPTIONS.map(({ label, key, isInput }) => {
          const findFilter = getSessionMatchFilter(value, key)
          return key === 'service_name' && !!serviceName ? null : (
            <FilterItem
              key={key}
              startTime={startTime}
              endTime={endTime}
              fieldName={key}
              label={_$(label)}
              op={findFilter?.Op}
              values={findFilter?.Values}
              filters={matchFilters}
              disabled={key === 'service_name' && !!serviceName}
              isInput={isInput}
              onChange={handleMatchChange}
            />
          )
        })}
        {SESSION_RANGE_OPTIONS.map(({ label, key, suffix }) => {
          const rangeFilter = getSessionRangeFilter(value, key)
          return (
            <FilterRangeItem
              key={key}
              fieldKey={key}
              label={_$(label)}
              start={rangeFilter?.Start ?? 0}
              end={rangeFilter?.End ?? 0}
              suffix={suffix}
              onChange={handleRangeChange}
            />
          )
        })}
      </div>
    </div>
  )
}

export default styled(SessionSearch)`
  width: 200px;
  padding-right: 12px;
  height: 100%;
  .session-search-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    span {
      font-size: 14px;
      font-weight: 500;
    }
    button {
      padding: 0;
    }
  }
`
